import { useDark, useToggle } from '@vueuse/core'
import { ref } from 'vue'

import { defineStore } from 'pinia'

const DEFAULT_PRIMARY_HEX = '#9b5ff5'

const normalizeHex = (value: string) => {
  const sanitized = value.trim().replace('#', '').toLowerCase()
  if (!/^[0-9a-f]{3}([0-9a-f]{3})?$/.test(sanitized)) return null

  if (sanitized.length === 3) {
    return `#${sanitized
      .split('')
      .map((char) => `${char}${char}`)
      .join('')}`
  }

  return `#${sanitized}`
}

const hexToRgb = (hex: string) => {
  const normalized = normalizeHex(hex)
  if (!normalized) return null

  const raw = normalized.replace('#', '')
  const parsed = Number.parseInt(raw, 16)

  return {
    r: (parsed >> 16) & 255,
    g: (parsed >> 8) & 255,
    b: parsed & 255,
  }
}

const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b]
    .map((value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, '0'))
    .join('')}`

const mixColor = (hex: string, target: '#ffffff' | '#000000', weight: number) => {
  const base = hexToRgb(hex)
  const mixTarget = hexToRgb(target)
  if (!base || !mixTarget) return hex

  const ratio = Math.max(0, Math.min(1, weight))

  return rgbToHex({
    r: base.r + (mixTarget.r - base.r) * ratio,
    g: base.g + (mixTarget.g - base.g) * ratio,
    b: base.b + (mixTarget.b - base.b) * ratio,
  })
}

const getUserThemeKey = () => {
  try {
    const extensionRaw = localStorage.getItem('extension')
    if (extensionRaw) {
      const parsed = JSON.parse(extensionRaw) as { number?: string; id?: string }
      const key = parsed.number || parsed.id
      if (key) return `hub.theme.primary.${String(key).trim()}`
    }
  } catch {}

  return 'hub.theme.primary.default'
}

export const useThemeStore = defineStore('theme', () => {
  const isDark = useDark({ selector: 'html' })
  const toggleDark = useToggle(isDark)
  const primaryColor = ref(DEFAULT_PRIMARY_HEX)

  const applyPrimaryPalette = (hex: string) => {
    const normalized = normalizeHex(hex) || DEFAULT_PRIMARY_HEX
    const root = document.documentElement

    const palette = {
      50: mixColor(normalized, '#ffffff', 0.9),
      100: mixColor(normalized, '#ffffff', 0.8),
      200: mixColor(normalized, '#ffffff', 0.65),
      300: mixColor(normalized, '#ffffff', 0.45),
      400: mixColor(normalized, '#ffffff', 0.25),
      500: normalized,
      600: mixColor(normalized, '#000000', 0.12),
      700: mixColor(normalized, '#000000', 0.24),
      800: mixColor(normalized, '#000000', 0.38),
      900: mixColor(normalized, '#000000', 0.52),
      950: mixColor(normalized, '#000000', 0.65),
    } as const

    root.style.setProperty('--p-primary-color', palette[500])
    root.style.setProperty('--p-primary-50', palette[50])
    root.style.setProperty('--p-primary-100', palette[100])
    root.style.setProperty('--p-primary-200', palette[200])
    root.style.setProperty('--p-primary-300', palette[300])
    root.style.setProperty('--p-primary-400', palette[400])
    root.style.setProperty('--p-primary-500', palette[500])
    root.style.setProperty('--p-primary-600', palette[600])
    root.style.setProperty('--p-primary-700', palette[700])
    root.style.setProperty('--p-primary-800', palette[800])
    root.style.setProperty('--p-primary-900', palette[900])
    root.style.setProperty('--p-primary-950', palette[950])

    root.style.setProperty('--color-primary-50', palette[50])
    root.style.setProperty('--color-primary-100', palette[100])
    root.style.setProperty('--color-primary-200', palette[200])
    root.style.setProperty('--color-primary-300', palette[300])
    root.style.setProperty('--color-primary-400', palette[400])
    root.style.setProperty('--color-primary-500', palette[500])
    root.style.setProperty('--color-primary-600', palette[600])
    root.style.setProperty('--color-primary-700', palette[700])
    root.style.setProperty('--color-primary-800', palette[800])
    root.style.setProperty('--color-primary-900', palette[900])
    root.style.setProperty('--color-primary-950', palette[950])
  }

  const setPrimaryColor = (hex: string) => {
    const normalized = normalizeHex(hex)
    if (!normalized) return

    primaryColor.value = normalized
    applyPrimaryPalette(normalized)

    try {
      localStorage.setItem(getUserThemeKey(), normalized)
    } catch {}
  }

  const resetPrimaryColor = () => {
    primaryColor.value = DEFAULT_PRIMARY_HEX
    applyPrimaryPalette(DEFAULT_PRIMARY_HEX)
    try {
      localStorage.removeItem(getUserThemeKey())
    } catch {}
  }

  const initUserTheme = () => {
    let resolvedColor = DEFAULT_PRIMARY_HEX

    try {
      const saved = localStorage.getItem(getUserThemeKey())
      const normalized = saved ? normalizeHex(saved) : null
      if (normalized) {
        resolvedColor = normalized
      }
    } catch {}

    primaryColor.value = resolvedColor
    applyPrimaryPalette(resolvedColor)
  }

  return {
    isDark,
    toggleDark,
    primaryColor,
    setPrimaryColor,
    resetPrimaryColor,
    initUserTheme,
  }
})
